import React, { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@/hooks/useNostr';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useQuery } from '@tanstack/react-query';
import { Tweet } from '@/components/tweet/Tweet';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Bell, Heart, Repeat, Mail, UserPlus, Zap } from 'lucide-react';

export function NotificationsPage() {
  useSeoMeta({
    title: 'Notifications / Twittstr',
    description: 'Track responses, mentions, reposts, zaps, and reactions gathered across Nostr networks.',
  });

  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [activeTab, setActiveTab] = useState<'all' | 'mentions'>('all');

  // Query notifications where 'p' tag references user pubkey
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications', user?.pubkey, activeTab],
    queryFn: async () => {
      if (!nostr || !user) return [];
      
      const kinds = activeTab === 'all' ? [1, 6, 7] : [1]; // Mentions are kind 1 notes referencing pubkey
      const items = await nostr.query([{ kinds, '#p': [user.pubkey], limit: 30 }]);
      
      // Filter out original tweets from self that are returned
      return items.filter(e => e.pubkey !== user.pubkey);
    },
    enabled: !!nostr && !!user,
  });

  const getNotificationIcon = (kind: number) => {
    switch (kind) {
      case 7:
        return <Heart className="size-5 text-pink-600 fill-pink-600" />;
      case 6:
        return <Repeat className="size-5 text-green-500" />;
      default:
        return <Bell className="size-5 text-sky-500 fill-sky-500" />;
    }
  };

  const getNotificationDesc = (kind: number, raw: string) => {
    switch (kind) {
      case 7:
        return 'liked your post';
      case 6:
        return 'reposted your post';
      default:
        return 'mentioned you in a post';
    }
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-background">
      {/* Search Header */}
      <header className="sticky top-0 bg-background/85 backdrop-blur-md z-30 border-b border-border flex flex-col pt-3 pb-0">
        <span className="font-extrabold text-xl px-4 pb-2 text-foreground">Notifications</span>
        
        <Tabs defaultValue="all" className="w-full" onValueChange={(val) => setActiveTab(val as any)}>
          <TabsList className="w-full flex h-12 bg-transparent p-0 justify-center">
            <TabsTrigger value="all" className="flex-1 h-full text-[15px] font-bold rounded-none border-b-4 border-transparent data-[state=active]:border-sky-500 data-[state=active]:text-foreground text-muted-foreground/80 cursor-pointer hover:bg-secondary/20">
              All
            </TabsTrigger>
            <TabsTrigger value="mentions" className="flex-1 h-full text-[15px] font-bold rounded-none border-b-4 border-transparent data-[state=active]:border-sky-500 data-[state=active]:text-foreground text-muted-foreground/80 cursor-pointer hover:bg-secondary/20">
              Mentions
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </header>

      {/* Main notifications list container */}
      <div className="flex flex-col divide-y divide-border">
        {!user ? (
          <div className="p-8 text-center flex flex-col items-center justify-center gap-3">
            <Card className="border-dashed bg-secondary/15 w-full max-w-sm rounded-2xl py-8">
              <CardContent className="flex flex-col items-center justify-center text-center gap-3 p-4">
                <Bell className="size-8 text-sky-500" />
                <p className="font-extrabold text-[15px]">Sign in to verify activity</p>
                <p className="text-xs text-muted-foreground leading-normal">
                  Connect using extension, local keypair or NIP-46 signers to synchronize mention events.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : isLoading ? (
          Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="p-4 flex gap-3 border-b border-border animate-pulse">
              <Skeleton className="size-10 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3 rounded-md" />
                <Skeleton className="h-4 w-3/4 rounded-md" />
              </div>
            </div>
          ))
        ) : notifications.length > 0 ? (
          notifications.map((item) => (
            <div key={item.id} className="flex gap-4 p-4 hover:bg-secondary/10 transition-colors border-b border-border">
              {/* Type indicator Icon */}
              <div className="shrink-0 pt-1">
                {getNotificationIcon(item.kind)}
              </div>
              
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="font-extrabold text-[15px]">@{item.pubkey.slice(0, 8)}...</span>
                  <span className="text-[14px] text-muted-foreground">{getNotificationDesc(item.kind, item.content)}</span>
                </div>
                
                {/* Embedded Tweet Context */}
                <div className="mt-2 border border-border/70 rounded-2xl overflow-hidden bg-secondary/5">
                  <Tweet event={item} />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="p-12 text-center text-muted-foreground text-sm">
            You don't have any notifications yet. When people mention, like, or repost your notes, it will appear here!
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
